<template>
  <div class="add-inventory-psi-container">
    <!-- 盘点单页面 -->
    <transition name="el-zoom-in-center">
      <div class="inventory-detail" v-show="showInventoryDetail">
        <!-- 页面头部 -->
        <div class="inventory-header">
          <div class="header-left">
            <el-button type="text" icon="el-icon-arrow-left" @click="goBack">
              返回
            </el-button>
            <h1>盘点单详情</h1>
          </div>
          <div class="header-right">
            <span class="task-info">任务：{{ taskName }}</span>
            <span class="warehouse-info">仓库：{{ warehouseName }}</span>
            <el-tag :type="inventoryStatusType">{{ inventoryStatusText }}</el-tag>
          </div>
        </div>

        <!-- 搜索和筛选区域 -->
        <div class="search-section">
          <el-form :model="searchForm" inline class="search-form">
            <el-form-item label="商品编码">
              <el-input 
                v-model="searchForm.productCode" 
                placeholder="请输入商品编码"
                clearable>
              </el-input>
            </el-form-item>
            
            <el-form-item label="商品名称">
              <el-input 
                v-model="searchForm.productName" 
                placeholder="请输入商品名称"
                clearable>
              </el-input>
            </el-form-item>
            
            <el-form-item label="商品分类">
              <el-select v-model="searchForm.category" placeholder="请选择分类" clearable>
                <el-option label="西药" value="western"></el-option>
                <el-option label="中药" value="chinese"></el-option>
                <el-option label="医疗器械" value="medical"></el-option>
              </el-select>
            </el-form-item>
            
            <el-form-item>
              <el-button type="primary" @click="handleSearch">查询</el-button>
              <el-button @click="resetSearch">重置</el-button>
            </el-form-item>
          </el-form>
          
          <div class="action-buttons">
            <el-button type="primary" @click="batchSave" :disabled="!hasChanges">
              批量保存
            </el-button>
            <el-button type="success" @click="submitInventory" :disabled="!canSubmit">
              提交盘点
            </el-button>
            <el-button @click="exportData">导出数据</el-button>
            <el-button type="text" @click="showStatistics">统计信息</el-button>
          </div>
        </div>

        <!-- 盘点数据表格 -->
        <div class="inventory-table-section">
          <el-table
            :data="inventoryData"
            border
            stripe
            style="width: 100%"
            height="calc(100vh - 300px)"
            v-loading="loading"
            @selection-change="handleSelectionChange">
            
            <el-table-column type="selection" width="55" align="center"></el-table-column>
            <el-table-column type="index" label="序号" width="60" align="center"></el-table-column>
            
            <el-table-column prop="productCode" label="商品编码" width="120" fixed="left">
              <template slot-scope="scope">
                <span class="product-code">{{ scope.row.productCode }}</span>
              </template>
            </el-table-column>
            
            <el-table-column prop="productName" label="商品名称" width="200" fixed="left">
              <template slot-scope="scope">
                <div class="product-info">
                  <span class="product-name">{{ scope.row.productName }}</span>
                  <span class="product-spec">{{ scope.row.specifications }}</span>
                </div>
              </template>
            </el-table-column>
            
            <el-table-column prop="category" label="分类" width="100" align="center">
              <template slot-scope="scope">
                <el-tag size="small">{{ getCategoryText(scope.row.category) }}</el-tag>
              </template>
            </el-table-column>
            
            <el-table-column prop="unit" label="单位" width="80" align="center"></el-table-column>
            
            <el-table-column prop="currentStock" label="当前库存" width="120" align="right">
              <template slot-scope="scope">
                <span class="stock-number">{{ scope.row.currentStock }}</span>
              </template>
            </el-table-column>
            
            <el-table-column prop="inventoryCount" label="盘点数量" width="150" align="center">
              <template slot-scope="scope">
                <el-input-number
                  v-model="scope.row.inventoryCount"
                  :min="0"
                  :precision="2"
                  :step="1"
                  size="mini"
                  controls-position="right"
                  @change="handleCountChange(scope.row)"
                  :class="{ 'changed': scope.row.inventoryCount !== scope.row.currentStock }">
                </el-input-number>
              </template>
            </el-table-column>
            
            <el-table-column prop="difference" label="差异数量" width="120" align="right">
              <template slot-scope="scope">
                <span :class="getDifferenceClass(scope.row.difference)">
                  {{ scope.row.difference > 0 ? '+' : '' }}{{ scope.row.difference }}
                </span>
              </template>
            </el-table-column>
            
            <el-table-column prop="differenceAmount" label="差异金额" width="120" align="right">
              <template slot-scope="scope">
                <span :class="getDifferenceClass(scope.row.difference)">
                  ¥{{ scope.row.differenceAmount.toFixed(2) }}
                </span>
              </template>
            </el-table-column>
            
            <el-table-column prop="unitPrice" label="单价" width="100" align="right">
              <template slot-scope="scope">
                ¥{{ scope.row.unitPrice.toFixed(2) }}
              </template>
            </el-table-column>
            
            <el-table-column prop="batchNumber" label="批号" width="120"></el-table-column>
            
            <el-table-column prop="productionDate" label="生产日期" width="120" align="center">
              <template slot-scope="scope">
                {{ formatDate(scope.row.productionDate) }}
              </template>
            </el-table-column>
            
            <el-table-column prop="expiryDate" label="有效期" width="120" align="center">
              <template slot-scope="scope">
                {{ formatDate(scope.row.expiryDate) }}
              </template>
            </el-table-column>
            
            <el-table-column prop="manufacturer" label="生产厂家" width="180" show-overflow-tooltip></el-table-column>
            
            <el-table-column prop="remark" label="备注" width="150">
              <template slot-scope="scope">
                <el-input
                  v-model="scope.row.remark"
                  size="mini"
                  placeholder="请输入备注"
                  @blur="handleRemarkChange(scope.row)">
                </el-input>
              </template>
            </el-table-column>
            
            <el-table-column label="操作" width="120" fixed="right" align="center">
              <template slot-scope="scope">
                <el-button 
                  type="text" 
                  size="mini" 
                  @click="showProductDetail(scope.row)"
                  icon="el-icon-view">
                  详情
                </el-button>
                <el-button 
                  type="text" 
                  size="mini" 
                  @click="resetRow(scope.row)"
                  :disabled="scope.row.inventoryCount === scope.row.currentStock">
                  重置
                </el-button>
              </template>
            </el-table-column>
          </el-table>
        </div>

        <!-- 分页和统计信息 -->
        <div class="footer-section">
          <div class="pagination">
            <el-pagination
              :current-page="pagination.currentPage"
              :page-size="pagination.pageSize"
              :total="pagination.total"
              layout="total, prev, pager, next, jumper"
              @current-change="handlePageChange">
            </el-pagination>
          </div>
          
          <div class="statistics">
            <span class="stat-item">已盘点：{{ statistics.counted }}/{{ statistics.total }}</span>
            <span class="stat-item">有差异：{{ statistics.different }}</span>
            <span class="stat-item">差异金额：¥{{ statistics.differenceAmount.toFixed(2) }}</span>
          </div>
        </div>
      </div>
    </transition>

    <!-- 商品详情对话框 -->
    <el-dialog
      title="商品详情"
      :visible.sync="showProductDialog"
      width="600px">
      <product-detail :product="currentProduct" v-if="showProductDialog"></product-detail>
    </el-dialog>

    <!-- 统计信息对话框 -->
    <el-dialog
      title="盘点统计信息"
      :visible.sync="showStatisticsDialog"
      width="500px">
      <statistics-panel :statistics="statistics"></statistics-panel>
    </el-dialog>

    <!-- 提交确认对话框 -->
    <el-dialog
      title="提交盘点"
      :visible.sync="showSubmitDialog"
      width="400px">
      <div class="submit-confirm">
        <p>确定要提交盘点结果吗？提交后将无法修改。</p>
        <p class="warning-text">请确保所有数据已核对无误！</p>
      </div>
      <span slot="footer">
        <el-button @click="showSubmitDialog = false">取消</el-button>
        <el-button type="primary" @click="confirmSubmit" :loading="submitting">确定提交</el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
// 导入子组件
import ProductDetail from './ProductDetail.vue'
import StatisticsPanel from './StatisticsPanel.vue'

export default {
  name: 'AddInventoryPsi',
  components: {
    ProductDetail,
    StatisticsPanel
  },
  data() {
    return {
      // 页面显示状态
      showInventoryDetail: true,
      
      // 任务信息
      taskName: '',
      warehouseName: '',
      inventoryStatus: 'counting', // counting: 盘点中, completed: 已完成
      
      // 搜索表单
      searchForm: {
        productCode: '',
        productName: '',
        category: ''
      },
      
      // 表格数据
      inventoryData: [],
      selectedRows: [],
      loading: false,
      
      // 分页信息
      pagination: {
        currentPage: 1,
        pageSize: 20,
        total: 0
      },
      
      // 统计信息
      statistics: {
        total: 0,
        counted: 0,
        different: 0,
        differenceAmount: 0
      },
      
      // 对话框状态
      showProductDialog: false,
      showStatisticsDialog: false,
      showSubmitDialog: false,
      submitting: false,
      
      // 当前选中的商品
      currentProduct: null,
      
      // 是否有未保存的更改
      hasChanges: false
    }
  },
  
  computed: {
    // 盘点状态类型
    inventoryStatusType() {
      return this.inventoryStatus === 'counting' ? 'warning' : 'success'
    },
    
    // 盘点状态文本
    inventoryStatusText() {
      return this.inventoryStatus === 'counting' ? '盘点中' : '已完成'
    },
    
    // 是否可以提交
    canSubmit() {
      return this.statistics.counted > 0 && this.hasChanges
    }
  },
  
  created() {
    this.initPage()
    this.loadInventoryData()
  },
  
  methods: {
    // 初始化页面
    initPage() {
      const { taskName, taskData } = this.$route.params
      this.taskName = taskName || '未命名任务'
      this.warehouseName = taskData?.warehouse === 'tech_park' ? '科技园仓库' : 
                          taskData?.warehouse === 'center_warehouse' ? '中心仓库' : '分店仓库'
    },
    
    // 加载盘点数据
    async loadInventoryData() {
      this.loading = true
      try {
        // 模拟API调用
        await new Promise(resolve => setTimeout(resolve, 1000))
        
        // 模拟数据
        this.inventoryData = this.generateMockData()
        this.pagination.total = this.inventoryData.length
        this.updateStatistics()
      } catch (error) {
        this.$message.error('加载盘点数据失败')
      } finally {
        this.loading = false
      }
    },
    // 在 generateMockData 方法后添加统计详细数据的方法
    generateDetailStatistics() {
      return [
        { category: '西药', total: 15, counted: 12, different: 3, amount: 156.80 },
        { category: '中药', total: 8, counted: 8, different: 2, amount: 89.50 },
        { category: '医疗器械', total: 5, counted: 3, different: 1, amount: 45.00 }
      ]
    },
    
    // 在 updateStatistics 方法中补充详细统计
    updateStatistics() {
      const data = this.inventoryData
      this.statistics.total = data.length
      this.statistics.counted = data.filter(item => item.inventoryCount !== null).length
      this.statistics.different = data.filter(item => item.difference !== 0).length
      this.statistics.differenceAmount = data.reduce((sum, item) => sum + item.differenceAmount, 0)
      this.statistics.detail = this.generateDetailStatistics()
      
      // 检查是否有更改
      this.hasChanges = data.some(item => item.inventoryCount !== item.currentStock)
    },
    // 生成模拟数据
    generateMockData() {
      const products = [
        { code: 'YP001', name: '阿莫西林胶囊', spec: '0.25g*24粒', category: 'western', price: 15.80 },
        { code: 'YP002', name: '板蓝根颗粒', spec: '10g*20袋', category: 'chinese', price: 12.50 },
        { code: 'YP003', name: '血压计', spec: '电子式', category: 'medical', price: 89.00 },
        { code: 'YP004', name: '维生素C片', spec: '100mg*60片', category: 'western', price: 8.90 },
        { code: 'YP005', name: '六味地黄丸', spec: '200丸', category: 'chinese', price: 25.00 }
      ]
      
      return products.map((product, index) => {
        const currentStock = Math.floor(Math.random() * 100) + 10
        const inventoryCount = currentStock // 初始等于当前库存
        const difference = 0
        const differenceAmount = 0
        
        return {
          id: index + 1,
          productCode: product.code,
          productName: product.name,
          specifications: product.spec,
          category: product.category,
          unit: product.category === 'medical' ? '台' : '盒',
          currentStock: currentStock,
          inventoryCount: inventoryCount,
          difference: difference,
          differenceAmount: differenceAmount,
          unitPrice: product.price,
          batchNumber: `BATCH${String(index + 1).padStart(3, '0')}`,
          productionDate: new Date(2024, 0, 15 + index),
          expiryDate: new Date(2026, 0, 15 + index),
          manufacturer: index % 2 === 0 ? '华北制药' : '云南白药',
          remark: ''
        }
      })
    },
    
    
    // 处理数量变化
    handleCountChange(row) {
      row.difference = row.inventoryCount - row.currentStock
      row.differenceAmount = row.difference * row.unitPrice
      this.updateStatistics()
    },
    
    // 处理备注变化
    handleRemarkChange(row) {
      // 可以在这里添加保存备注的逻辑
      console.log('备注已更新:', row.remark)
    },
    
    // 获取分类文本
    getCategoryText(category) {
      const map = {
        'western': '西药',
        'chinese': '中药',
        'medical': '医疗器械'
      }
      return map[category] || category
    },
    
    // 获取差异样式类
    getDifferenceClass(difference) {
      if (difference > 0) return 'positive-diff'
      if (difference < 0) return 'negative-diff'
      return 'no-diff'
    },
    
    // 格式化日期
    formatDate(date) {
      if (!date) return ''
      const d = new Date(date)
      const year = d.getFullYear()
      const month = String(d.getMonth() + 1).padStart(2, '0')
      const day = String(d.getDate()).padStart(2, '0')
      return `${year}-${month}-${day}`
    },
    
    // 返回上一页
    goBack() {
      if (this.hasChanges) {
        this.$confirm('有未保存的更改，确定要返回吗？', '提示', {
          type: 'warning'
        }).then(() => {
          this.$router.go(-1)
        })
      } else {
        this.$router.go(-1)
      }
    },
    
    // 处理搜索
    handleSearch() {
      this.pagination.currentPage = 1
      this.loadInventoryData()
    },
    
    // 重置搜索
    resetSearch() {
      this.searchForm = {
        productCode: '',
        productName: '',
        category: ''
      }
      this.handleSearch()
    },
    
    // 处理选择变化
    handleSelectionChange(selection) {
      this.selectedRows = selection
    },
    
    // 处理分页变化
    handlePageChange(page) {
      this.pagination.currentPage = page
      this.loadInventoryData()
    },
    
    // 批量保存
    batchSave() {
      this.$message.success('数据保存成功')
      this.hasChanges = false
    },
    
    // 显示商品详情
    showProductDetail(product) {
      this.currentProduct = product
      this.showProductDialog = true
    },
    
    // 显示统计信息
    showStatistics() {
      this.showStatisticsDialog = true
    },
    
    // 重置单行数据
    resetRow(row) {
      this.$confirm('确定要重置该商品的盘点数量吗？', '提示', {
        type: 'warning'
      }).then(() => {
        row.inventoryCount = row.currentStock
        this.handleCountChange(row)
      })
    },
    
    // 提交盘点
    submitInventory() {
      this.showSubmitDialog = true
    },
    
    // 确认提交
    async confirmSubmit() {
      this.submitting = true
      try {
        // 模拟提交过程
        await new Promise(resolve => setTimeout(resolve, 2000))
        
        this.$message.success('盘点结果提交成功')
        this.inventoryStatus = 'completed'
        this.showSubmitDialog = false
        this.hasChanges = false
      } catch (error) {
        this.$message.error('提交失败')
      } finally {
        this.submitting = false
      }
    },
    
    // 导出数据
    exportData() {
      this.$message.info('导出功能开发中')
    }
  }
}
</script>

<style lang="less" scoped>
.add-inventory-psi-container {
  min-height: 100vh;
  background: #f5f7fa;
  padding: 20px;
}

.inventory-detail {
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.1);
  overflow: hidden;
}

.inventory-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px 30px;
  background: linear-gradient(135deg, #409EFF 0%, #66b1ff 100%);
  color: white;
  
  .header-left {
    display: flex;
    align-items: center;
    
    h1 {
      margin: 0 0 0 15px;
      font-size: 24px;
    }
    
    .el-button {
      color: white;
      font-size: 16px;
      
      &:hover {
        background: rgba(255, 255, 255, 0.1);
      }
    }
  }
  
  .header-right {
    display: flex;
    align-items: center;
    gap: 20px;
    
    .task-info, .warehouse-info {
      font-size: 14px;
    }
    
    .el-tag {
      font-size: 14px;
      padding: 5px 10px;
    }
  }
}

.search-section {
  padding: 20px 30px;
  border-bottom: 1px solid #ebeef5;
  
  .search-form {
    margin-bottom: 15px;
    
    .el-form-item {
      margin-bottom: 10px;
      margin-right: 20px;
    }
  }
  
  .action-buttons {
    display: flex;
    justify-content: flex-end;
    gap: 10px;
  }
}

.inventory-table-section {
  padding: 0 30px;
}

.footer-section {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 15px 30px;
  border-top: 1px solid #ebeef5;
  background: #fafafa;
  
  .statistics {
    display: flex;
    gap: 20px;
    
    .stat-item {
      font-size: 14px;
      color: #606266;
      font-weight: 500;
    }
  }
}

// 表格样式
.product-code {
  font-family: 'Courier New', monospace;
  font-weight: bold;
  color: #409EFF;
}

.product-info {
  display: flex;
  flex-direction: column;
  
  .product-name {
    font-weight: 500;
    margin-bottom: 2px;
  }
  
  .product-spec {
    font-size: 12px;
    color: #909399;
  }
}

.stock-number {
  font-weight: bold;
  color: #303133;
}

// 差异样式
.positive-diff {
  color: #67C23A;
  font-weight: bold;
}

.negative-diff {
  color: #F56C6C;
  font-weight: bold;
}

.no-diff {
  color: #909399;
}

// 输入框变化样式
.changed {
  /deep/ .el-input__inner {
    border-color: #409EFF;
    background-color: #f0f9ff;
  }
}

.submit-confirm {
  text-align: center;
  
  p {
    margin: 10px 0;
    line-height: 1.5;
  }
  
  .warning-text {
    color: #e6a23c;
    font-weight: 500;
  }
}

// 响应式设计
@media (max-width: 768px) {
  .add-inventory-psi-container {
    padding: 10px;
  }
  
  .inventory-header {
    flex-direction: column;
    align-items: flex-start;
    padding: 15px 20px;
    
    .header-right {
      margin-top: 10px;
      flex-wrap: wrap;
      gap: 10px;
    }
  }
  
  .search-section {
    padding: 15px 20px;
    
    .action-buttons {
      flex-wrap: wrap;
      justify-content: flex-start;
    }
  }
  
  .inventory-table-section {
    padding: 0 10px;
  }
  
  .footer-section {
    flex-direction: column;
    gap: 10px;
    align-items: stretch;
    
    .statistics {
      justify-content: space-around;
    }
  }
}

// 过渡动画
.el-zoom-in-center-enter-active,
.el-zoom-in-center-leave-active {
  transition: all 0.3s ease;
}

.el-zoom-in-center-enter,
.el-zoom-in-center-leave-to {
  opacity: 0;
  transform: scale(0.95);
}
</style>